// Foyer glue, not a port. Two small, pure helpers that let
// SignageEngineSurface resolve PLAYER-6/Verimatrix DRM for a protected
// signage asset - a real, Foyer-only need with no autoplayer analogue
// (DrmConfig.ts's own doc comment: "Foyer resolves DRM for signage assets
// separately via playbackLicence.ts and adapts the result into this same
// shape at the call site." This file IS that call site.)
//
// ## Why this needs no wire-schema change at all
//
// The ported engine's own `MediaAsset` (from the legacy wire schedule) has
// no `contentAssetId` - `legacySignageSchedule.ts` never carries it, and
// extending that schema was the invasive option this gap was originally
// scoped around. It turns out unnecessary: `_id: item.id` on EVERY wire
// asset (confirmed by direct read of both `toLegacyAsset` and
// `breakItemToLegacyAsset`) means a `MediaAsset.id` the ported machine
// reports as "current" is always the SAME id as some `SignageItem` in
// Foyer's own resolved `SignageTimeline` - which DOES carry
// `contentAssetId`. `findSignageItemById` is that join, searched fresh
// against the current timeline rather than trusted from `playheadAt` (whose
// own resolution only covers `timeline.content`/`timeline.playlists`, not
// `timeline.breaks` - a protected item playing during a campaign break
// would be invisible to `playheadAt` but must still resolve a licence).
import type { SignageItem, SignageTimeline } from "@shared/types/signage";
import type { DrmConfig } from "../signage-engine/types/DrmConfig";
import type { ResolvedItemPlayback } from "./playbackLicence";

/**
 * Finds the `SignageItem` with this id anywhere in the timeline - an
 * ordinary content loop's playlist, or a campaign break's own item list.
 * `undefined` when nothing matches (a stale id from a schedule that has
 * since moved on, or an id that never came from this timeline at all).
 */
export function findSignageItemById(
  timeline: SignageTimeline,
  id: string
): SignageItem | undefined {
  for (const playlist of timeline.playlists) {
    const hit = playlist.items.find((i) => i.id === id);
    if (hit) return hit;
  }
  for (const brk of timeline.breaks) {
    const hit = brk.items.find((i) => i.id === id);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Adapts `playbackLicence.ts`'s own result shape into `ShakaPlayer.tsx`'s
 * `DrmConfig` - the two are deliberately different (`ResolvedItemPlayback`
 * is the play route's own response shape; `DrmConfig` is what the ported
 * player component reads), and this is the one place that reconciles them.
 *
 * `certificateUrl: ""` for both systems, always - PLAYER-6/Verimatrix
 * signage DRM carries no privacy-mode service certificate today (confirmed
 * by reading SignageSurface.tsx's own inline Shaka-configure effect: it
 * builds `servers` from `licenceUrl`/`playreadyLicenceUrl` alone, no
 * `advanced`/certificate at all - that concept is Redbee/live-TV-only in
 * this codebase). `ShakaPlayer.tsx` only adds the `advanced` certificate
 * config when `certificateUrl` is truthy, so an empty string here is a
 * no-op there, matching current production behaviour exactly rather than
 * guessing at a field nothing populates.
 */
export function toDrmConfig(
  licensed: ResolvedItemPlayback | null
): DrmConfig | undefined {
  if (!licensed) return undefined;
  const widevine = licensed.licenceUrl
    ? { licenseServerUrl: licensed.licenceUrl, certificateUrl: "" }
    : undefined;
  const playready = licensed.playreadyLicenceUrl
    ? { licenseServerUrl: licensed.playreadyLicenceUrl, certificateUrl: "" }
    : undefined;
  if (!widevine && !playready) return undefined;
  return { widevine, playready };
}
